import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from "recharts";
import { ExpenseComposition } from "@/hooks/useDREMonthlyData";
import { PieChartIcon } from "lucide-react";

interface ExpenseCompositionChartProps {
  data: ExpenseComposition[];
  total: number;
}

const COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(220 70% 50%)",
  "hsl(280 70% 50%)",
  "hsl(340 70% 50%)",
];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(value / 100);
}

export function ExpenseCompositionChart({ data, total }: ExpenseCompositionChartProps) {
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <PieChartIcon className="h-4 w-4" />
            Composição de Despesas
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center text-muted-foreground">
          Sem despesas para exibir
        </CardContent>
      </Card>
    );
  }

  // Take top 7 and group the rest as "Outras"
  const sortedData = [...data].sort((a, b) => b.amount - a.amount);
  const topItems = sortedData.slice(0, 7);
  const otherItems = sortedData.slice(7);
  
  const chartData = [...topItems];
  if (otherItems.length > 0) {
    const otherTotal = otherItems.reduce((sum, item) => sum + item.amount, 0);
    chartData.push({
      category: "Outras",
      amount: otherTotal,
      percentage: total > 0 ? (otherTotal / total) * 100 : 0,
    });
  }

  const chartConfig = chartData.reduce((acc, item, index) => {
    acc[item.category] = {
      label: item.category,
      color: COLORS[index % COLORS.length],
    };
    return acc;
  }, {} as Record<string, { label: string; color: string }>);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <PieChartIcon className="h-4 w-4" />
          Composição de Despesas
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              dataKey="amount"
              nameKey="category"
              label={({ category, percentage }) => `${category.substring(0, 10)}${category.length > 10 ? '...' : ''} ${percentage.toFixed(1)}%`}
              labelLine={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value, name) => (
                    <div className="flex flex-col">
                      <span className="font-medium">{name}</span>
                      <span>{formatCurrency(Number(value))}</span>
                    </div>
                  )}
                />
              }
            />
          </PieChart>
        </ChartContainer>
        <div className="mt-4 text-center text-sm text-muted-foreground">
          Total: <span className="font-medium text-foreground">{formatCurrency(total)}</span>
        </div>
      </CardContent>
    </Card>
  );
}
